// Arabic numeral normalization utilities.
//
// Users typing in Arabic on iOS/Android often hit the ٠-٩ digit row on
// their keyboard, but every form validator / backend contract expects
// ASCII digits (0-9). Instead of forcing users to switch keyboards
// mid-input, we transparently convert their Arabic (٠-٩) and Persian
// (۰-۹) digits to ASCII at the DOM level — before React's synthetic
// event handler reads the value.
//
// Two hooks are installed on `document`:
//   1. beforeinput (capture) — required for `<input type="number">`
//      because those reject non-ASCII characters at the native input
//      level BEFORE any `input` event fires. We preventDefault + splice
//      the normalized character in manually via the React-aware setter.
//   2. input (capture) — fallback for paste, autofill, dispatchEvent,
//      and any surface where beforeinput doesn't fire cleanly (older
//      Safari, IME composition, etc).

// Both Arabic-Indic (Arabic) and Extended Arabic-Indic (Persian/Urdu).
const AR_TO_EN = {
  "٠": "0", "١": "1", "٢": "2", "٣": "3", "٤": "4",
  "٥": "5", "٦": "6", "٧": "7", "٨": "8", "٩": "9",
  "۰": "0", "۱": "1", "۲": "2", "۳": "3", "۴": "4",
  "۵": "5", "۶": "6", "۷": "7", "۸": "8", "۹": "9",
};
const AR_DIGIT_RE = /[٠-٩۰-۹]/g;

export function normalizeArabicDigits(str) {
  if (typeof str !== "string" || !str) return str;
  if (!AR_DIGIT_RE.test(str)) return str;         // fast path
  AR_DIGIT_RE.lastIndex = 0;                      // reset for replace
  return str.replace(AR_DIGIT_RE, (ch) => AR_TO_EN[ch] || ch);
}

// Types where digit normalization does NOT apply:
// - password:      value should not be silently rewritten
// - date/time/etc: native pickers, browser handles digits
// - checkbox/radio/file/color/range: no user text input
const SKIP_TYPES = new Set([
  "password", "date", "time", "datetime-local", "month", "week",
  "file", "checkbox", "radio", "color", "range", "hidden", "submit",
  "button", "image", "reset",
]);

function nativeValueSetter(el) {
  const proto = el instanceof HTMLTextAreaElement
    ? HTMLTextAreaElement.prototype
    : HTMLInputElement.prototype;
  return Object.getOwnPropertyDescriptor(proto, "value").set;
}

function isTargetInput(el) {
  if (!el) return false;
  if (el.tagName !== "INPUT" && el.tagName !== "TEXTAREA") return false;
  if (SKIP_TYPES.has(el.type)) return false;
  return true;
}

// One-time installer — attaches capture-phase listeners so we run
// BEFORE React's synthetic event pipeline AND before the browser's
// own `<input type="number">` validation kicks in.
export function installArabicNumeralNormalizer() {
  if (typeof document === "undefined") return;
  if (document.__arabicNumeralNormalizerInstalled) return;
  document.__arabicNumeralNormalizerInstalled = true;

  // ── Hook 1: beforeinput ─────────────────────────────────────────
  // Fires BEFORE the browser writes the character. Critical path for
  // <input type="number"> where non-ASCII chars are silently dropped
  // otherwise. We check `event.data` (the incoming string, may be a
  // single keystroke or a longer paste chunk) and if it contains
  // Arabic/Persian digits we cancel the native insert and manually
  // splice the ASCII form into the value.
  document.addEventListener("beforeinput", (e) => {
    const el = e.target;
    if (!isTargetInput(el)) return;
    const data = e.data;
    if (!data) return;
    const norm = normalizeArabicDigits(data);
    if (data === norm) return;
    e.preventDefault();
    // For <input type="number">, `el.value` may report "" when the
    // current text is invalid; fall back to defaultValue in that case.
    const cur = el.type === "number" && el.value === "" && el.validity && el.validity.badInput
      ? ""
      : el.value;
    const start = el.selectionStart ?? cur.length;
    const end = el.selectionEnd ?? cur.length;
    const next = cur.slice(0, start) + norm + cur.slice(end);
    nativeValueSetter(el).call(el, next);
    try { el.setSelectionRange(start + norm.length, start + norm.length); } catch {}
    // Fire input/change so React state syncs. `data` mirrors the
    // ASCII we inserted so React validators see 0-9.
    el.dispatchEvent(new InputEvent("input", { bubbles: true, data: norm, inputType: e.inputType }));
  }, true);

  // ── Hook 2: input (fallback) ────────────────────────────────────
  // Catches paste / autofill / programmatic changes that skip
  // beforeinput. If Arabic digits somehow made it into the value
  // (older browsers, IME) rewrite in-place. Guarded so it never
  // fights beforeinput's already-clean insertions.
  document.addEventListener("input", (e) => {
    const el = e.target;
    if (!isTargetInput(el)) return;
    const orig = el.value;
    const norm = normalizeArabicDigits(orig);
    if (orig === norm) return;
    const s = el.selectionStart, en = el.selectionEnd;
    nativeValueSetter(el).call(el, norm);
    try { el.setSelectionRange(s, en); } catch {}
  }, true);
}
